import React, { useEffect, useMemo, useState } from 'react';
import { Input } from './ui/input';
import {
  OTHER_DEPARTMENT_LABEL,
  OTHER_DEPARTMENT_VALUE,
  STANDARD_DEPARTMENTS,
  mergeDepartmentOptions,
} from '../data/departments';

/**
 * Department dropdown with standard enterprise list + Other / Custom free-text.
 *
 * value / onChange use the final department name string (never "__other__").
 */
export default function DepartmentSelect({
  value = '',
  onChange,
  companyDepartments = [],
  choices = STANDARD_DEPARTMENTS,
  id = 'department-select',
  label = 'Department',
  required = false,
  dark = false,
  className = '',
  selectClassName = '',
  'data-testid': testId = 'department-select',
}) {
  const options = useMemo(
    () => mergeDepartmentOptions([...(choices || []), ...companyDepartments]),
    [choices, companyDepartments]
  );

  const isKnown = value && options.includes(value);
  const [mode, setMode] = useState(value ? (isKnown ? value : OTHER_DEPARTMENT_VALUE) : '');
  const [custom, setCustom] = useState(value && !isKnown ? value : '');

  useEffect(() => {
    if (!value) {
      setMode('');
      setCustom('');
      return;
    }
    if (options.includes(value)) {
      setMode(value);
      setCustom('');
    } else {
      setMode(OTHER_DEPARTMENT_VALUE);
      setCustom(value);
    }
  }, [value, options]);

  const selectCls = dark
    ? 'w-full rounded-xl bg-slate-950/60 border border-white/15 text-slate-100 text-sm px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-400/50'
    : 'w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500';

  const handleSelect = (next) => {
    setMode(next);
    if (next === OTHER_DEPARTMENT_VALUE) {
      onChange?.(custom.trim());
      return;
    }
    setCustom('');
    onChange?.(next);
  };

  const handleCustom = (text) => {
    setCustom(text);
    onChange?.(text);
  };

  return (
    <div className={className} data-testid={testId}>
      {label ? (
        <label htmlFor={id} className={`block text-xs font-semibold uppercase tracking-wider mb-2 ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
          {label}
          {required ? ' *' : ''}
        </label>
      ) : null}
      <select
        id={id}
        value={mode}
        required={required && mode !== OTHER_DEPARTMENT_VALUE}
        onChange={(e) => handleSelect(e.target.value)}
        className={`${selectCls} ${selectClassName}`}
        data-testid={`${testId}-dropdown`}
      >
        <option value="" disabled>
          Select a department
        </option>
        {options.map((name) => (
          <option key={name} value={name}>
            {name}
          </option>
        ))}
        <option value={OTHER_DEPARTMENT_VALUE}>{OTHER_DEPARTMENT_LABEL}</option>
      </select>

      {mode === OTHER_DEPARTMENT_VALUE && (
        <Input
          value={custom}
          onChange={(e) => handleCustom(e.target.value)}
          placeholder="Type your custom department name"
          required={required}
          className={`mt-2 ${dark ? 'bg-slate-950/60 border-white/15 text-slate-100 placeholder:text-slate-500' : ''}`}
          data-testid={`${testId}-custom`}
        />
      )}
    </div>
  );
}
